import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import Grid from '@material-ui/core/Grid';
import Hidden from '@material-ui/core/Hidden';
import SeeAllLink from 'components/SeeAllLink';
import _isEmpty from 'lodash/isEmpty';
import ListCarousel from 'containers/EditorList/components/ListCarousel';
import AppContainer from 'components/AppContainer';
import AssessmentSmallCardList from 'components/AssessmentSmallCardList';
import _get from 'lodash/get';
import { makeStyles } from '@material-ui/core/styles';
import {
  makeSelectClientLanding,
  makeSelectPageLanding,
} from 'containers/ClientLanding/selectors';
import {
  makeSelectClientDetails,
  makeSelectHomeHeaders,
} from 'containers/Main/selectors';
import PersonalizationList from 'components/PersonalizationList';
import useWindowSize from 'components/useWindowSize';
import useSiteCopySelector from 'components/useSiteCopySelector';
import { sortClientLandingList } from './helpers';
import { useGetLandingLists, useGetClientLists } from './hooks';

const useStyles = makeStyles(theme => ({
  root: {
    marginTop: 14,
    [theme.breakpoints.down('480')]: {
      position: 'relative',
    },
  },
  viewMore: {
    display: 'flex',
    justifyContent: 'center',
  },
}));

function EditorsList({ grayscale, isLanding, useClientImage }) {
  const classes = useStyles();

  const [resourceItemSiteCopy] = useSiteCopySelector(['resource-item-page']);
  const { width } = useWindowSize();

  // ==== HEADER DETAILS ====
  const homeHeaders = useSelector(makeSelectHomeHeaders());
  const linkLabel = _get(homeHeaders, 'pageCopy.listLinkLabel');

  // ==== LANDING DETAILS ====
  const clientLanding = useSelector(makeSelectClientLanding());
  const pageLanding = useSelector(makeSelectPageLanding());
  const finalLanding = isLanding ? pageLanding : clientLanding;
  const clientListIds = _get(finalLanding, 'listsCollection.items', [])
    .map(item => _get(item, 'sys.id'))
    .filter(item => Boolean(item));
  const clientAudienceIds = _get(
    finalLanding,
    'audienceTypeCollection.items',
    [],
  )
    .filter(item => ['Accepted', 'Internal'].includes(item.sys.id))
    .map(item => item.name)
    .filter(item => Boolean(item));
  const listCount = _get(finalLanding, 'pageText.listCount', 3);

  // ==== CLIENT DETAILS ====
  const clientDetails = useSelector(makeSelectClientDetails());
  const clientId = _get(clientDetails, 'sys.id');
  const clientShortName = _get(clientDetails, 'shortName');
  const clientExcludedTopic = _get(
    clientDetails,
    'excludeTopicCollection.items',
  );
  const clientExcludedCost = _get(
    clientDetails,
    'metadata.hideCostDescriptionResources',
  );
  const clientExcludedResourceTypes =
    _get(clientDetails, 'excludeResourceTypes') || [];
  const averageRatingCutoff = _get(clientDetails, 'averageRatingCutoff');
  const expertRatingCutoff = _get(clientDetails, 'expertRatingCutoff');
  const userRatingCutoff = _get(clientDetails, 'userRatingCutoff');
  const excludedAssessments = _get(
    clientDetails,
    'excludeAssessmentCollection.items',
  );
  const algoliaResourceTypeMapping = _get(
    resourceItemSiteCopy,
    ['pageCopy', 'algoliaResourceTypeMapping'],
    {},
  );
  const assessmentsDurationAltPosition = _get(
    clientDetails,
    'metadata.homeAssessmentsTimeAltPosition',
    false,
  );

  // ==== QUERY HOOKS ====
  const { data: landingLists, status } = useGetLandingLists({
    clientId,
    clientDetails,
    clientShortName,
    clientExcludedTopic,
    clientExcludedResourceTypes,
    clientExcludedCost,
    clientListIds,
    clientAudienceIds,
    listCount,
    averageRatingCutoff,
    expertRatingCutoff,
    userRatingCutoff,
    excludedAssessments,
    algoliaResourceTypeMapping,
  });

  const {
    data: { aboveFeatureList, belowFeatureList },
  } = useGetClientLists(clientId);

  const sortingOrder = _get(clientLanding, 'listSortLogic');
  const clientLandingList = sortClientLandingList(landingLists, sortingOrder);

  if (status === 'loading' || _isEmpty(landingLists)) {
    return null;
  }

  const renderList = list => {
    if (_isEmpty(list)) return null;

    const filteredList = list.filter(
      item =>
        _get(item, 'resources', []).length > 0 ||
        _get(item, 'fields.resources', []).length > 0,
    );

    // ? Temporary for Midday
    const isSmallCard = filteredList.some(
      item => _get(item, 'fields.style') === 'Small Card',
    );

    return (
      <AppContainer spacing={2} justify="flex-start">
        {filteredList.map(item => {
          const resources = _get(item, 'fields.resources', []);
          const hasTopicsListsOnly =
            resources.filter(
              resource =>
                _get(resource, 'sys.contentType.sys.id') === 'topics' ||
                _get(resource, 'sys.contentType.sys.id') === 'list',
            ).length === resources.length;

          if (
            hasTopicsListsOnly &&
            _get(item, 'fields.style') === 'Alt Topic' &&
            width > 480 &&
            _get(item, 'fields.resources').length <= 6
          )
            return (
              <PersonalizationList
                key={_get(item, 'fields.slug')}
                item={item}
                linkLabel={linkLabel}
                grayscale={grayscale}
              />
            );

          if (_get(item, 'fields.style') === 'Small Card')
            return (
              <AssessmentSmallCardList
                key={_get(item, 'fields.slug')}
                data={item}
              />
            );

          return (
            <ListCarousel
              data={item}
              key={_get(item, 'fields.slug')}
              linkLabel={linkLabel}
              personalizedCard={_get(item, 'fields.style') === 'Alt Topic'}
              grayscale={grayscale}
              useClientImage={useClientImage}
              assessmentsDurationAltPosition={assessmentsDurationAltPosition}
            />
          );
        })}

        {// ? Should hide if using "Small Card" as style
        !isSmallCard && (
          <Hidden smUp>
            <Grid item xs={12} className={classes.viewMore}>
              <SeeAllLink
                link="lists"
                isMobile
                bordered
                align="center"
                label={linkLabel}
              />
            </Grid>
          </Hidden>
        )}
      </AppContainer>
    );
  };

  return (
    <>
      {renderList(aboveFeatureList)}
      {renderList(clientLandingList)}
      {renderList(belowFeatureList)}
    </>
  );
}

EditorsList.propTypes = {
  grayscale: PropTypes.bool,
  isLanding: PropTypes.bool,
  useClientImage: PropTypes.bool,
};

export default EditorsList;
